import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Sparkle, ArrowRight, ArrowClockwise } from "@phosphor-icons/react";
import { PLAN_RANK } from "@/lib/planPresentation";
import { track } from "@/lib/analytics";

// Deterministic recommendation — informational only. Never subscribes/upgrades.
const QUESTIONS = [
  { key: "who", q: "Who is this for?", opts: [["me", "Just me"], ["family", "My family"], ["business", "My business / team"]] },
  { key: "storage", q: "How much do you store?", opts: [["light", "A few documents"], ["medium", "Lots of documents"], ["heavy", "Very large archive"]] },
  { key: "sign", q: "Do you need e-signatures?", opts: [["no", "No"], ["yes", "Yes — DuruSign"]] },
  { key: "verify", q: "Do you need identity/document verification?", opts: [["no", "No"], ["yes", "Yes — DuruVerify / ID Check"]] },
  { key: "collab", q: "Do you collaborate with others?", opts: [["no", "Mostly solo"], ["family", "With family"], ["team", "With a team / deal rooms"]] },
];

function recommend(a) {
  // Map answers to the minimum tier that satisfies the strongest need.
  let plan = "basic";
  const reasons = [];
  if (a.who === "business" || a.collab === "team") { plan = "business"; reasons.push("you work with a team and need DuruRooms, audit logs & compliance"); }
  else if (a.verify === "yes") { plan = "premium"; reasons.push("you need DuruVerify / ID Check (early access on Premium)"); }
  else if (a.who === "family" || a.collab === "family" || a.storage === "heavy") { plan = "family"; reasons.push("you need lots of storage and to manage documents for your family"); }
  else if (a.sign === "yes" || a.storage === "medium") { plan = "personal"; reasons.push("you need DuruSign & DuruTwin with generous storage"); }
  else { plan = "basic"; reasons.push("you mainly need secure storage, DuruScan, password-protected links and expiration reminders"); }
  return { plan, reason: reasons[0] };
}

export default function SmartPlanFinder({ plans = [], theme = "light", onSelect, mismatch = false }) {
  const dark = theme === "dark";
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [ans, setAns] = useState({});
  const [rec, setRec] = useState(null);

  const start = () => { setOpen(true); setStep(0); setAns({}); setRec(null); track("plan_finder_started"); };
  const pick = (key, val) => {
    const next = { ...ans, [key]: val };
    setAns(next);
    if (step + 1 < QUESTIONS.length) setStep(step + 1);
    else { const r = recommend(next); setRec(r); track("plan_finder_completed"); track("plan_recommended", { plan_id: r.plan }); }
  };
  const recPlan = rec ? plans.find((p) => p.plan_id === rec.plan) : null;

  const card = dark ? "border-white/12 bg-white/[0.03]" : "border-slate-200 bg-white";
  const muted = dark ? "text-white/55" : "text-slate-500";
  const optBtn = dark ? "border-white/15 hover:border-primary hover:bg-primary/10 text-white/90" : "border-slate-200 hover:border-primary hover:bg-primary/5";

  return (
    <div className={`rounded-2xl border p-6 mb-8 ${card}`} data-testid="smart-plan-finder">
      {!open && !rec && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h3 className="font-display text-lg font-semibold flex items-center gap-2"><Sparkle size={20} weight="fill" className="text-primary" /> What do you want DuruVault to do for you?</h3>
            <p className={`text-sm mt-1 ${muted}`}>Answer 5 quick questions and we'll suggest the right plan.</p>
          </div>
          <Button onClick={start} className="rounded-full shrink-0" data-testid="plan-finder-start-btn">Find my plan <ArrowRight size={16} className="ml-1.5" /></Button>
        </div>
      )}

      {open && !rec && (
        <div data-testid="plan-finder-step">
          <div className="flex items-center justify-between mb-3">
            <span className={`text-xs ${muted}`}>Step {step + 1} of {QUESTIONS.length}</span>
          </div>
          <h3 className="font-semibold text-base mb-3">{QUESTIONS[step].q}</h3>
          <div className="grid sm:grid-cols-3 gap-2">
            {QUESTIONS[step].opts.map(([val, label]) => (
              <button key={val} onClick={() => pick(QUESTIONS[step].key, val)} data-testid={`finder-opt-${QUESTIONS[step].key}-${val}`}
                className={`text-left rounded-xl border px-4 py-3 text-sm transition-all ${optBtn}`}>{label}</button>
            ))}
          </div>
        </div>
      )}

      {rec && (
        <div data-testid="plan-finder-result">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-primary font-semibold"><Sparkle size={14} weight="fill" /> Recommended for you</div>
          <h3 className="font-display text-2xl font-bold mt-1" data-testid="finder-recommended-plan">{recPlan?.name || rec.plan}</h3>
          <p className={`text-sm mt-1 ${muted}`}>Recommended for you because {rec.reason}.</p>
          <div className="flex flex-wrap gap-2 mt-4">
            {recPlan && onSelect && recPlan.plan_id !== "free" && recPlan.purchasable && !mismatch && (
              <Button onClick={() => onSelect(recPlan)} className="rounded-full" data-testid="finder-choose-recommended">Choose {recPlan.name} <ArrowRight size={16} className="ml-1.5" /></Button>
            )}
            {recPlan && mismatch && recPlan.plan_id !== "free" && (
              <Button disabled variant="outline" className="rounded-full" data-testid="finder-choose-disabled">Switch country to buy</Button>
            )}
            <Button onClick={start} variant="outline" className={`rounded-full ${dark ? "bg-transparent border-white/20 text-white hover:bg-white/10" : ""}`} data-testid="plan-finder-restart-btn"><ArrowClockwise size={15} className="mr-1.5" /> Start over</Button>
          </div>
        </div>
      )}
    </div>
  );
}
